import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Copy, Check, ThumbsUp, ThumbsDown, Crown, Target } from "lucide-react";
import { classNames } from "@/lib/format";
import { toast } from "sonner";

function ScoreRing({ score }) {
  const pct = (Math.max(0, Math.min(10, score)) / 10) * 100;
  const color = score >= 8 ? "hsl(var(--emerald))" : score >= 5 ? "hsl(var(--amber))" : "hsl(var(--rose))";
  return (
    <div className="relative h-20 w-20 shrink-0" data-testid="remix-score">
      <div className="h-full w-full rounded-full" style={{ background: `conic-gradient(${color} ${pct}%, hsl(var(--muted)) 0)` }} />
      <div className="absolute inset-[6px] rounded-full bg-card flex items-center justify-center">
        <span className="font-mono text-2xl font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

export default function RemixResult({ item }) {
  const r = item.result;
  const [copied, setCopied] = useState(null);
  const copy = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(i);
    toast.success("Hook copied");
    setTimeout(() => setCopied(null), 1500);
  };
  const best = Number(r.best_pick ?? 0);

  return (
    <div className="space-y-4" data-testid="remix-result">
      <Card className="p-5">
        <div className="flex items-start gap-5">
          <ScoreRing score={Number(r.score)} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Detected pattern</div>
            <div className="font-display font-bold text-lg" data-testid="remix-detected-style">{r.detected_style}</div>
            <p className="text-sm mt-1.5 leading-relaxed" data-testid="remix-verdict">{r.verdict}</p>
            {r.pattern_match?.pattern && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1">
                <Target className="h-3.5 w-3.5" /> Closest hot pattern: {r.pattern_match.pattern}
              </div>
            )}
            {r.pattern_match?.note && <p className="text-xs text-muted-foreground mt-2">{r.pattern_match.note}</p>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5 pt-5 border-t border-border">
          <div>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium text-[hsl(var(--emerald))] mb-2"><ThumbsUp className="h-3.5 w-3.5" /> Strengths</div>
            <ul className="space-y-1 text-sm">{(r.strengths || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-muted-foreground">•</span>{s}</li>)}</ul>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium text-[hsl(var(--rose))] mb-2"><ThumbsDown className="h-3.5 w-3.5" /> Weaknesses</div>
            <ul className="space-y-1 text-sm">{(r.weaknesses || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-muted-foreground">•</span>{s}</li>)}</ul>
          </div>
        </div>
      </Card>

      <div>
        <h3 className="font-display font-bold text-lg mb-3">Rewrites</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="remix-rewrites">
          {(r.rewrites || []).map((rw, i) => (
            <Card key={i} className={classNames("p-4 relative", i === best && "border-primary/60 ring-1 ring-primary/30")} data-testid={`remix-rewrite-${i}`}>
              {i === best && (
                <div className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                  <Crown className="h-3 w-3" /> Top pick
                </div>
              )}
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{rw.style}</div>
              <div className="font-display text-base font-bold leading-snug mt-1">“{rw.hook}”</div>
              <p className="text-xs text-muted-foreground mt-2">{rw.why}</p>
              <button onClick={() => copy(rw.hook, i)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`remix-copy-${i}`}>
                {copied === i ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === i ? "Copied" : "Copy hook"}
              </button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
